import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useRef, useState } from "react";
import { Mic, Layers } from "lucide-react";

export default function CaseStudies() {
  const { ref, isVisible } = useScrollReveal();
  const scrollContainerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  const scrollContainerRef2 = useRef(null);
  const [isDragging2, setIsDragging2] = useState(false);
  const [startX2, setStartX2] = useState(0);
  const [scrollLeft2, setScrollLeft2] = useState(0);


  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white py-20 lg:py-32 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-8 4xl:px-16 xxs:pb-0`}
    >
      {/* Mobile Case Studies - header + scrollable image */}
      <div className="mx-auto lg:hidden">
        {/* Mobile header - above scrollable image */}
        <div className="mb-6">
          <h1 className="text-3xl xxs:text-[55px] font-normal text-black enfinity-header-font leading-tight mb-4" style={{ lineHeight: "1.2" }}>
            <span className="text-[#6d6ed0] enfinity-header-font">EnfiniShield</span>
            <br />
            <span>success</span>
            <br />
            <span>stories</span>
          </h1>
          <p className="text-base xxs:text-[1rem] lg:text-base text-black leading-relaxed enfinity-subtext-font max-w-xl">
            EnfiniShield identifies stolen content and helps recover leaked earnings through protection and enforcement across platforms.
          </p>
        </div>
        {/* Mobile - horizontal scrollable EnfiniShield image (same as desktop) */}
        <div
          className="mt-2 -mx-4 overflow-x-auto overflow-y-hidden pb-4 touch-pan-x [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          <div className="min-w-max">
            <img
              src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/s-scroll.png"
              alt="EnfiniShield Case Studies"
              className="h-auto max-h-[390px] object-contain object-top max-w-[3500px] pointer-events-none p-4"
              draggable={false}
            />
          </div>
        </div>
        {/* Mobile - EnfiniBoost section under scrollable image */}
        <div className="mt-16">
          <h2 className="text-3xl xxs:text-[55px] font-normal text-black enfinity-header-font leading-tight mb-4" style={{ lineHeight: "1.2" }}>
            <span className="text-[#3b82f6] enfinity-header-font">EnfiniBoost</span>
            <br />
            <span>success</span>
            <br />
            <span>stories</span>
          </h2>
          <p className="text-base xxs:text-[1rem] lg:text-base text-black leading-relaxed enfinity-subtext-font max-w-xl">
          EnfiniBoost turns strong videos into wider reach and additional revenue, instead of relying on a single viral moment.
          </p>
        </div>
        {/* Mobile - horizontal scrollable EnfiniBoost image (same as desktop) */}
        <div
          className="mt-6 -mx-4 overflow-x-auto overflow-y-hidden pb-4 touch-pan-x [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
        >
          <div className="min-w-max">
            <img
              src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/b-scroll.png"
              alt="EnfiniBoost Case Studies"
              className="h-auto max-h-[390px] object-contain object-top max-w-[5000px] pointer-events-none p-4"
              draggable={false}
            />
          </div>
        </div>

        {/* Mobile - EnfiniTask section under EnfiniBoost scrollable */}
        <div className="mt-16 text-left">
          <h2 className="text-3xl xxs:text-[55px] font-normal text-black enfinity-header-font leading-tight mb-4" style={{ lineHeight: "1.2" }}>
            <span className="text-[#8b5cf6] enfinity-header-font">EnfiniTask</span>
            <br />
            <span>case studies</span>
          </h2>
          <p className="text-base xxs:text-[1rem] lg:text-base text-black leading-relaxed enfinity-subtext-font max-w-xl mb-6">
          EnfiniTask gives creators the support they need to keep publishing without burning out. From editors to assistants, it helps teams run smoothly as channels grow.
          </p>
          <img
            src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/task-image.png"
            alt="EnfiniTask case studies"
            className="w-full h-auto object-contain"
          />
        </div>
      </div>

      {/* Desktop Case Studies Content */}
      <div className="mx-auto hidden lg:block max-w-7xl">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
          <div className="flex-1">
            <h1 className="text-5xl md:text-6xl lg:text-[60px] xl:text-[80px] 4xl:text-[90px] font-normal text-black leading-[106px] enfinity-header-font" style={{ lineHeight: '1.2' }}>
              <span className="text-[#6d6ed0] enfinity-header-font">EnfiniShield</span>
              <br />
              <span>success stories</span>
            </h1>
          </div>
          <div className="lg:w-[565px] flex-shrink-0">
            <p className="text-xl font-normal text-black leading-[34px] text-left lg:text-right pt-4 lg:pt-[140px] xl:pt-[110px] 1xl:pt-[40px] 2xl:pt-[40px] 4xl:pt-[95px] 4xl:text-[20px] enfinity-subtext-font">
            EnfiniShield identifies stolen content and helps recover leaked earnings through protection and enforcement across platforms.
            </p>
          </div>
        </div>

        {/* Scrollable Case Studies */}
        <div className="relative -mx-4 lg:-mx-6 xl:-mx-8">
          <div
            ref={scrollContainerRef}
            className="relative overflow-x-auto pb-8 cursor-grab active:cursor-grabbing select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={(e) => {
              setIsDragging(true);
              setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
              setScrollLeft(scrollContainerRef.current.scrollLeft);
            }}
            onMouseLeave={() => setIsDragging(false)}
            onMouseUp={() => setIsDragging(false)}
            onMouseMove={(e) => {
              if (!isDragging) return;
              e.preventDefault();
              const x = e.pageX - scrollContainerRef.current.offsetLeft;
              const walk = (x - startX) * 2;
              scrollContainerRef.current.scrollLeft = scrollLeft - walk;
            }}
          >
            <div className="min-w-max">
              <img
                src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/s-scroll.png"
                alt="EnfiniShield Case Studies"
                className="h-auto object-contain max-w-[3500px] pointer-events-none p-10"
                draggable={false}
              />
            </div>
          </div>
        </div>

        {/* EnfiniBoost Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12 mt-16">
          <div className="flex-1">
            <h1 className="text-5xl md:text-6xl lg:text-[60px] xl:text-[80px] 4xl:text-[90px] font-normal text-black leading-[106px] enfinity-header-font" style={{ lineHeight: '1.2' }}>
              <span className="text-[#3b82f6] enfinity-header-font">EnfiniBoost</span>
              <br />
              <span>success stories</span>
            </h1>
          </div>
          <div className="lg:w-[600px] 4xl:w-[450px] flex-shrink-0">
            <p className="text-xl font-normal text-black leading-[34px] text-left lg:text-right pt-4 lg:pt-[140px] xl:pt-[110px] 1xl:pt-[40px] 2xl:pt-[40px] 4xl:pt-[100px] 4xl:text-[20px] enfinity-subtext-font">
            EnfiniBoost turns strong videos into wider reach and additional revenue, instead of relying on a single viral moment.
            </p>
          </div>
        </div>

        {/* Scrollable EnfiniBoost Case Studies */}
        <div className="relative -mx-4 lg:-mx-6 xl:-mx-8">
          <div
            ref={scrollContainerRef2}
            className="relative overflow-x-auto pb-8 cursor-grab active:cursor-grabbing select-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={(e) => {
              setIsDragging2(true);
              setStartX2(e.pageX - scrollContainerRef2.current.offsetLeft);
              setScrollLeft2(scrollContainerRef2.current.scrollLeft);
            }}
            onMouseLeave={() => setIsDragging2(false)}
            onMouseUp={() => setIsDragging2(false)}
            onMouseMove={(e) => {
              if (!isDragging2) return;
              e.preventDefault();
              const x = e.pageX - scrollContainerRef2.current.offsetLeft;
              const walk = (x - startX2) * 2;
              scrollContainerRef2.current.scrollLeft = scrollLeft2 - walk;
            }}
          >
            <div className="min-w-max">
              <img
                src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/b-scroll.png"
                alt="EnfiniBoost Case Studies"
                className="h-auto object-contain max-w-[5700px] pointer-events-none p-10"
                draggable={false}
              />
            </div>
          </div>
        </div>

        {/* EnfiniTask Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12 mt-16">
          <div className="flex-1">
            <h1 className="text-5xl md:text-6xl lg:text-[60px] xl:text-[80px] 4xl:text-[90px] font-normal text-black leading-[106px] enfinity-header-font enfinity-header-font" style={{ lineHeight: '1.2' }}>
              <span className="text-[#8b5cf6] enfinity-header-font enfinity-header-font">EnfiniTask</span>
              <br />
              <span>case studies</span>
            </h1>
          </div>
          <div className="lg:w-[595px] flex-shrink-0">
            <p className="text-xl font-normal text-black leading-[34px] text-left lg:text-right pt-0 lg:pt-[95px] xl:pt-[60px] 4xl:pt-[90px] 4xl:text-[20px] enfinity-subtext-font">
              EnfiniTask gives creators the support they need to keep publishing without burning out. From editors to assistants, it helps teams run smoothly as channels grow.
            </p>
          </div>
        </div>

        {/* Case Study Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12">
          {/* Multi-channel Case Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#8b5cf6] rounded-lg flex items-center justify-center flex-shrink-0">
                <Layers className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <h3 className="text-xl font-normal text-black enfinity-header-font">Multi-channel Case</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">$3.5K</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">New Monthly Revenue</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">240+</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">Hours Saved Per Month</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">6 active</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">Channels Managed</p>
              </div>
            </div>
            
            <p className="text-base text-black leading-relaxed enfinity-subtext-font">
              An MCN partnered with EnfiniTask to scale multi-channel content operations. With a dedicated team of certified specialists handling repetitive workflows, they now manage 6 channels efficiently, reclaim 8 hours daily for strategic growth, and have tripled their outsourcing ROI.
            </p>
          </div>

          {/* Twitch Streamer Case Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#8b5cf6] rounded-lg flex items-center justify-center flex-shrink-0">
                <Mic className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <h3 className="text-xl font-normal text-black enfinity-header-font">Twitch Streamer Case</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">$30K</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">New Monthly Revenue</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">50M+</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">Monthly Piracy Views</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#8b5cf6] mb-1 enfinity-header-font">4x</p>
                <p className="text-xs text-gray-500 uppercase enfinity-subtext-font">ROI</p>
              </div>
            </div>
            
            <p className="text-base text-black leading-relaxed enfinity-subtext-font">
              A leading Twitch gaming creator used EnfiniTask to fight organized piracy. A dedicated team removed unauthorized uploads, blocked 50M+ views, protected the brand, and recovered over $30,000 monthly while spending just $7,000 on on-demand talent.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
